import { APP_ROUTES } from '@routes/routes.constants';
import { useFlags } from 'launchdarkly-react-client-sdk';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import type { OrganizationListProps } from './OrganizationList.types';

import { OrganizationOption } from '../organization-option/OrganizationOption';
import styles from './OrganizationList.module.css';

const OrganizationList = React.memo(
  ({
    organizations,
    currentOrgId,
    isInternalUser,
    activeIndex,
    listboxId,
    onOrganizationSelect,
    onClose,
    onSetActiveIndex,
  }: Readonly<OrganizationListProps>): React.ReactElement => {
    const navigate = useNavigate();
    const { companyProfile } = useFlags();
    const showCompanyProfile = Boolean(companyProfile);

    const handleCompanyProfileClick = React.useCallback(
      (customerId: string): void => {
        onClose();
        navigate(
          APP_ROUTES.MANAGE_COMPANY_PROFILE.replace(':customerId', customerId)
        );
      },
      [navigate, onClose]
    );

    return (
      <ul
        id={listboxId}
        role="listbox"
        aria-label="Organizations"
        className={`${styles.organizationList} ${isInternalUser ? styles.organizationListInternal : ''}`}
        data-testid="organization-list"
      >
        {organizations.length === 0 ? (
          <li className={styles.noResults}>No results found</li>
        ) : (
          organizations.map((org, index) => (
            <OrganizationOption
              key={org.id}
              organization={org}
              isSelected={org.id === currentOrgId}
              isActive={index === activeIndex}
              isInternalUser={isInternalUser}
              showCompanyProfile={showCompanyProfile}
              onSelect={onOrganizationSelect}
              onCompanyProfileClick={handleCompanyProfileClick}
              index={index}
              onSetActiveIndex={onSetActiveIndex}
            />
          ))
        )}
      </ul>
    );
  }
);

OrganizationList.displayName = 'OrganizationList';

export { OrganizationList };
